import React, { createContext, useReducer } from 'react';
import { ACCESSORY, CONTRACT } from '../../../utils/constants';
import { recommendationSliceStore, UpdateCartSliceStore, AppProviderSchema, ActionSchema,
  AppContextType } from './types';

const initialState: recommendationSliceStore & UpdateCartSliceStore = {
  isFetchingRecommendedAccessories: true,
  recommendedAccessories: [],
  multiItemAccessoriessList:  {},
  selectedAccessories: {},
  deviceSessionValues: {},
  removeSelectedCartAccessories: {},
  selectedPaymentType: CONTRACT,
  selectedTab: ACCESSORY, // Initialize selectedTab with default value
  previousTab: ACCESSORY, // Initialize previousTab with default value
  showMiniCart: false, // Initialize mini cart collapsed
  loading: false,
  cart: {
    cartItem: [
      {
        itemType: '',
        items: [
          {
            action: '',
            id: '',  // Product ID
            contractTerm: 24, //cartItemData.contractTerm, // Contract Term
            note: []
          }
        ]
      }
    ]
  },
  updateAccessoryCartSuccess: {
    cartItem: [
      {
        itemType: '',
        items: [
          {
            action: '',
            id: '',
            contractTerm: 24,
            note: []
          }
        ]
      }
    ]
  },
  updateAccessoryCartFailure: {},
  cartData: null, // Initialize new state
  isLoadingCartData: false, // Initialize loading state
  cartDataError: null, // Initialize error state
};

export const actionTypes = {
  recommendedAccessories: 'setRecommendedAccessories',
  multiItemAccessoriessList: 'setMultiItemAccessoriessList',
  selectedAccessories: 'setSelectedAccessories',
  deviceSessionValues: 'setDeviceSessionValues',
  removeSelectedCartAccessories: 'setRemoveSelectedCartAccessories',
  selectedPaymentType: 'setSelectedPaymentType',
  selectedTab: 'setSelectedTab',
  previousTab: 'setPreviousTab',
  showMiniCart: 'setShowMiniCart',
  recommendedAccessoriesStart: 'FETCH_RECOMMENDED_ACCESSORIES_START',
  RecommendedAccessoriesSuccess: 'FETCH_RECOMMENDED_ACCESSORIES_SUCCESS',
  recommendedAccessoriesFailure: 'FETCH_RECOMMENDED_ACCESSORIES_FAILURE',
  updateAccessoryCartSuccess: 'setupdateAccessoryCartSuccess',
  updateAccessoryCartFailure: 'setupdateAccessoryCartFailure',
  updateAccessoryCartStart: 'UPDATE_ACCESSORY_CART_START',
  getCartStart: 'GET_CART_START',
  getCartSuccess: 'GET_CART_SUCCESS',
  getCartFailure: 'GET_CART_FAILURE',
};

export const AppContext = createContext<AppContextType | undefined>(undefined);

const appReducer = (store: recommendationSliceStore & UpdateCartSliceStore, action: ActionSchema) => {
  switch (action.type) {
    case actionTypes?.recommendedAccessories:
      return { ...store, recommendedAccessories: action?.payload };
    case actionTypes?.multiItemAccessoriessList:
      return { ...store, multiItemAccessoriessList: action?.payload };
    case actionTypes?.selectedAccessories:
      return { ...store, selectedAccessories: action?.payload };
    case actionTypes?.deviceSessionValues:
      return { ...store, deviceSessionValues: action?.payload };
    case actionTypes?.removeSelectedCartAccessories:
      return { ...store, removeSelectedCartAccessories: action?.payload };
    case actionTypes?.selectedPaymentType:
      return { ...store, selectedPaymentType: action?.payload };
    case actionTypes?.selectedTab:
      return { ...store, selectedTab: action?.payload };
    case actionTypes?.previousTab:
      return { ...store, previousTab: action?.payload };
    case actionTypes?.showMiniCart:
      return { ...store, showMiniCart: action?.payload };
    case actionTypes?.recommendedAccessoriesStart:
      return { ...store, isFetchingRecommendedAccessories: true };
    case actionTypes?.RecommendedAccessoriesSuccess:
      return { 
        ...store, 
        isFetchingRecommendedAccessories: false, 
        recommendedAccessories: {
          ...store.recommendedAccessories,
          ...action.payload
        }
      };
    case actionTypes?.recommendedAccessoriesFailure:
      return { ...store, isFetchingRecommendedAccessories: false, recommendedAccessories: [] };
    case actionTypes?.updateAccessoryCartSuccess:
      return { ...store, updateAccessoryCartSuccess: action?.payload, loading: false };
    case actionTypes?.updateAccessoryCartFailure:
      return { ...store, updateAccessoryCartFailure: action?.payload, loading: false };
    case actionTypes?.updateAccessoryCartStart:
      return { ...store, loading: true };
    case actionTypes?.getCartStart:
      return { ...store, isLoadingCartData: true, cartDataError: null };
    case actionTypes?.getCartSuccess:
      // processType and subscribedInfo are stored inside cartData itself
      // (preserved by mergeCartData helper in useMultiAccessoryUpdateCartActions)
      return { ...store, isLoadingCartData: false, cartData: action?.payload };
    case actionTypes?.getCartFailure:
      return { ...store, isLoadingCartData: false, cartDataError: action?.payload };
    default:
      return store;
  }
};

const MultiAccessoryProvider = ({ children }: AppProviderSchema) => {
  const [store, dispatch] = useReducer(appReducer, initialState);
  return <AppContext.Provider value={{ store, dispatch }}>{children}</AppContext.Provider>;
};

export default MultiAccessoryProvider;